<template>
  <div>
    <div  v-if="loadingSearch" class="contet-admin-record-data d-flex justify-content-center align-items-center">
      <b-spinner label="Loading" variant="primary" />
    </div>
    <template v-else>
      <template v-if="adminRecordData.length">
        <div class="contet-admin-record-data">
          <b-alert
            v-if="showAlert"
            show
            variant="danger"
            class="p-1 mb-1"
          >
            <feather-icon
              icon="AlertCircleIcon"
              size="18"
              style="margin-right: 5px;"
            />
            No hay cambios en la administración de grabaciones
          </b-alert>
          <p class="mb-2 h5">
            Velocidad de bits total (Límite/Max) {{ limitSpeedBits}} / {{ bitrateLimit }}
            <b-button
              class="ml-1"
              size="sm"
              variant="primary"
              v-b-tooltip.hover.button.viewport="`Actualizar`"
              @click="fetchAdminRecordData()"
            >
              <feather-icon icon="RefreshCwIcon" />
            </b-button>
          </p>
          <b-table
            class="mt-1"
            :items="adminRecordData"
            :fields="fields"
            sticky-header="400px"
            small
          >
            <template #cell(NormalMode)="data">
              <v-select
                v-model="data.item.NormalMode"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="modes"
                :reduce="(val) => val.value"
                :calculate-position="withPopper"
                :disabled="loading"
                append-to-body
                @input="updateField(data.item)"
              />
            </template>

            <template #cell(EventMode)="data">
              <v-select
                v-model="data.item.EventMode"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="modes"
                :reduce="(val) => val.value"
                :calculate-position="withPopper"
                :disabled="loading"
                append-to-body
                @input="updateField(data.item)"
              />
            </template>

            <template #cell(FullFrameBandWidth)="data">
              {{ frameData(data.item.FullFrameBandWidth, data.item.FullFrameRate) }}
            </template>

            <template #cell(KeyFrameBandWidth)="data">
              {{ frameData(data.item.KeyFrameBandWidth, data.item.KeyFrameRate) }}
            </template>

            <template #cell(BitrateLimit)="data">
              <div class="d-flex justify-content-center">
                <b-input-group append="M" style="width: 110px;">
                  <b-form-input
                    type="number"
                    v-model="data.item.BitrateLimit"
                    :min="bitrateData.min"
                    :max="bitrateData.max"
                    step="0.01"
                    :disabled="loading"
                    @change="validateBitrateLimit(data.item)"
                  />
                </b-input-group>
              </div>
            </template>

            <template #cell(PreEventDuration)="data">
              <v-select
                v-model="data.item.PreEventDuration"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="preEventDurations"
                :reduce="(val) => val.value"
                :calculate-position="withPopper"
                :disabled="loading"
                append-to-body
                @input="updateField(data.item)"
              />
            </template>

            <template #cell(PostEventDuration)="data">
              <v-select
                v-model="data.item.PostEventDuration"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="postEventDurations"
                :reduce="(val) => val.value"
                :calculate-position="withPopper"
                :disabled="loading"
                append-to-body
                @input="updateField(data.item)"
              />
            </template>

            <template #cell(AudioEnable)="data">
              <v-select
                v-model="data.item.AudioEnable"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="audioOptions"
                :reduce="(val) => val.value"
                :calculate-position="withPopper"
                :disabled="loading"
                append-to-body
                @input="updateField(data.item)"
              />
            </template>
          </b-table>
        </div>
        <div class="text-right">
          <button
            class="btn btn-primary"
            :disabled="loading"
            @click="saveAdminRecord()"
          >
            {{ buttonText }}
          </button>
        </div>
      </template>
      <div  v-else class="contet-admin-record-data">
        <b-alert
          show
          variant="warning"
          class="p-1 mb-1"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="18"
            style="margin-right: 5px;"
          />
          Sin información de administración de grabaciones
        </b-alert>
      </div>
    </template>
  </div>
</template>

<script>
import { computed,onMounted,ref } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@axios";

export default {
  props: {
    hanwhaDvr: {
      type: Object,
      require: true
    }
  },
  components:{
    vSelect
  },
  setup (props, { emit }) {
    //HOOKS
    onMounted(async () => await fetchAdminRecordData());

    //COMPOSABLES
    const toast = useToast();

    //DATA
    const adminRecordData = ref([]);

    const audioOptions = [
      {
        label: 'Apagado',
        value: false
      },
      {
        label: 'Encendido',
        value: true
      }
    ];

    const bitrateData = ref({
      min: 0,
      max: 0
    });

    const fields = ref([
      { key: 'Channel', label: 'Canal', thClass: 'text-center py-1', tdClass: 'text-center'},
      { key: 'NormalMode', label: 'Grabación continua',thClass: 'text-center py-1', tdClass: 'text-center'},
      { key: 'EventMode', label: 'Grabación de eventos',thClass: 'text-center py-1', tdClass: 'text-center'},
      { key: 'FullFrameBandWidth', label: 'Marco completo',thClass: 'text-center py-1', tdClass: 'text-center' },
      { key: 'KeyFrameBandWidth', label: 'Cuadro I',thClass: 'text-center py-1', tdClass: 'text-center' },
      { key: 'BitrateLimit', label: 'Límite',thClass: 'text-center py-1', tdClass: 'text-center' },
      { key: 'PreEventDuration', label: 'Pre evento',thClass: 'text-center py-1', tdClass: 'text-center' },
      { key: 'PostEventDuration', label: 'Post evento',thClass: 'text-center py-1', tdClass: 'text-center' },
      { key: 'AudioEnable', label: 'Audio',thClass: 'text-center py-1', tdClass: 'text-center' }
    ]);

    const loading = ref(false);

    const loadingSearch = ref(false);

    const modes = [
      {
        label: 'Apagado',
        value: 'Off'
      },
      {
        label: 'Completa',
        value: 'Full'
      },
      {
        label: 'Cuadro I',
        value: 'I-Frame'
      }
    ];

    const postEventDurations = [
      {
        label: 'Apagado',
        value: 'Off'
      },
      {
        label: '5 segundos',
        value: '5s'
      },
      {
        label: '10 segundos',
        value: '10s'
      },
      {
        label: '20 segundos',
        value: '20s'
      },
      {
        label: '30 segundos',
        value: '30s'
      },
      {
        label: '1 minuto',
        value: '1m'
      },
      {
        label: '3 minutos',
        value: '3m'
      },
      {
        label: '5 minutos',
        value: '5m'
      },
      {
        label: '10 minutos',
        value: '10m'
      },
      {
        label: '20 minutos',
        value: '20m'
      },
      {
        label: '1 hora',
        value: '1h'
      },
      {
        label: '2 hora',
        value: '2h'
      },
      {
        label: '3 hora',
        value: '3h'
      },
      {
        label: '4 hora',
        value: '4h'
      },
      {
        label: '5 hora',
        value: '5h'
      },
      {
        label: '6 hora',
        value: '6h'
      }
    ];

    const preEventDurations = [
      {
        label: 'Apagado',
        value: 'Off'
      },
      {
        label: '5 segundos',
        value: '5s'
      },
      {
        label: '10 segundos',
        value: '10s'
      },
      {
        label: '15 segundos',
        value: '15s'
      },
    ];

    const showAlert = ref(false);

    //COMPUTED
    const bitrateLimit = computed(() => bitrateData.value.max.toFixed(1));

    const buttonText = computed(() => (loading.value) ? 'Guardando...' : 'Guardar');

    const limitSpeedBits = computed(() => {
      const limits = adminRecordData.value.map(ard => Number(ard.BitrateLimit));
      const limitsTotal = limits.reduce((a, b) => a + b, 0);

      return limitsTotal.toFixed(1);
    });

    //METHODS
    const frameData = (n1, n2) => {
      if (!n1 || !n2) {
        return '-';
      }

      const numberFormatted1 = Number(n1).toFixed(1);
      const numberFormatted2 = Number(n2).toFixed(1);

      return `${numberFormatted1} M (${numberFormatted2})`
    }

    const fetchAdminRecordData = async () => {
      loadingSearch.value = true;

      if (!bitrateData.value.min && !bitrateData.value.max) {
        await getBitrateLimit();
      }

      await getAdminRecordData();

      loadingSearch.value = false;
    }

    const getAdminRecordData = async (activateLoading = false) => {
      const params = {
        id_dvr: props.hanwhaDvr.id
      }

      await axios.get('/v1/hanwha/dvr/admin/get_admin_record_data', { params })
        .then(async (response) => {
          const { data } = response;

          adminRecordData.value = data.data.RecordSetup.map(rs => ({
            ...rs,
            updated: false
          }));
        })
        .catch(() => {
          adminRecordData.value = [];
        });
    }

    const getBitrateLimit = async () => {
      const params = {
        id_dvr: props.hanwhaDvr.id
      }

      showAlert.value = false;

      await axios.get('/v1/hanwha/dvr/admin/get_bitrate', { params })
        .then(async (response) => {
          const { data } = response;

          bitrateData.value.min = data.data.bitrate_limit.min;
          bitrateData.value.max = data.data.bitrate_limit.max;
        })
        .catch(() => {
          bitrateData.value = {
            min: 0,
            max: 0
          }
        });
    }

    const saveAdminRecord = async () => {
    /*   await nextTick();
 */
      const recordSetupUpdates = adminRecordData.value.filter(ard => ard.updated);

      if (!recordSetupUpdates.length) {
        showAlert.value = true;
        return
      }

      showAlert.value = false;

      const request = {
        id_dvr: props.hanwhaDvr.id,
        record_setup: recordSetupUpdates
      }

      loading.value = true;

      await axios.post('/v1/hanwha/dvr/admin/set-record-data', request)
        .then(async (response) => {
          const { data } = response;

          toast({
            component: ToastificationContent,
            props: {
              title: data.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          });

          await fetchAdminRecordData();
        })
        .catch((error) => {
          const { response } = error;

          toast({
            component: ToastificationContent,
            props: {
              title: response.data.message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        })
        .finally(() => {
          loading.value = false;
        });
    }

    const updateField = (item) => {
      item.updated = true;
    }

    const validateBitrateLimit = (item) => {
      let value = Number(item.BitrateLimit);

      if (value > bitrateData.value.max) {
          value = bitrateData.value.max;
      }

      if (value < bitrateData.value.min) {
          value = bitrateData.value.min;
      }

      item.BitrateLimit = Number(value.toFixed(2));
      item.updated = true;
    }

    return {
      audioOptions,
      adminRecordData,
      bitrateData,
      fields,
      loading,
      loadingSearch,
      modes,
      postEventDurations,
      preEventDurations,
      showAlert,
      withPopper: popperHelper.withPopper,

      bitrateLimit,
      buttonText,
      limitSpeedBits,

      frameData,
      fetchAdminRecordData,
      saveAdminRecord,
      updateField,
      validateBitrateLimit
    }
  }
}
</script>

<style lang="scss" scoped>
.contet-admin-record-data {
  height: 400px;
}

.border-channel {
  border: 1px solid rgba(0, 0, 0, .13);
}
</style>
